import { yupResolver } from '@hookform/resolvers/yup';
import { useEffect, useRef } from 'react';
import { useForm } from 'react-hook-form';
import { useQueryClient } from 'react-query';
import * as Yup from 'yup';

import { IProgramSetup } from 'shared/interfaces/IProgramSetup';
import { useProgramSetupService } from 'shared/services/programSetupService';
import { Alert } from 'shared/theme/elements';
import { programSetupRecordToForm } from 'shared/utils/programSetupRecordToForm';
import { ChannelSettingsView } from './ChannelSettingsView';
import { getChannelRecording, IChannelRecording, RADIO_MACHINE_ITEMS, TV_MACHINE_ITEMS } from './channelRecording';

interface Props {
  mode: 'tv' | 'radio';
  channelId?: string;
  channelName?: string;
  // Any one program on the channel; the backend applies its recording config to all of them.
  representative?: IProgramSetup;
  loading?: boolean;
}

const ChannelSettingsSchema: Yup.SchemaOf<IChannelRecording> = Yup.object({
  machine: Yup.string().required().label('Recording Machine Name'),
  source: Yup.string().required().label('Source Folder'),
  folder: Yup.string().required().label('Folder Name'),
  ads: Yup.boolean().required().label('Ads Automation'),
});

const ChannelSettingsContainer = ({ mode, channelId, channelName, representative, loading }: Props) => {
  const user = localStorage?.getItem('user_admin') ? JSON.parse(localStorage.getItem('user_admin') || '') : null;
  const label = mode === 'tv' ? 'Channel' : 'Station';

  const queryClient = useQueryClient();
  const { updateProgramSetup } = useProgramSetupService();
  const { updateMutation } = updateProgramSetup();

  const form = useForm<IChannelRecording>({
    resolver: yupResolver(ChannelSettingsSchema),
    defaultValues: getChannelRecording(representative),
  });
  const { reset, handleSubmit, formState } = form;

  // Switching channel drops any unsaved edits — each channel keeps its own settings.
  const dirtyRef = useRef(false);
  dirtyRef.current = formState.isDirty;
  const prevChannelRef = useRef(channelId);
  useEffect(() => {
    if (prevChannelRef.current !== channelId && dirtyRef.current) {
      Alert.information(`Unsaved ${label.toLowerCase()} settings were discarded`);
    }
    prevChannelRef.current = channelId;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [channelId]);

  useEffect(() => {
    reset(getChannelRecording(representative));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [channelId, representative?._id, representative?.date_updated]);

  const handleRevert = () => reset(getChannelRecording(representative));

  const handleSave = handleSubmit((values: IChannelRecording) => {
    if (!representative) return;
    const base = programSetupRecordToForm(representative, mode);

    updateMutation.mutate(
      {
        data: {
          ...base,
          _id: representative._id,
          updated_by: user?._id,
          local_recording_details: {
            ...base.local_recording_details,
            local_recording_path: values.machine,
            source_folder: values.source,
          },
          recording_title_setup: {
            file_extension: null,
            recording_machine_name: null,
            filename: null,
            ...base.recording_title_setup,
            ...(mode === 'radio' ? { ocr_lang: base.recording_title_setup?.ocr_lang || 'eng' } : {}),
            folder_name: values.folder,
            ads_automation: values.ads,
          },
        },
        image: {},
      },
      {
        onSuccess: () => {
          Alert.success(`${label} Settings Saved!`, `Applied to every program on ${channelName || 'this channel'}.`);
          queryClient.invalidateQueries('program_setup');
        },
        onError: (error: any) => {
          Alert.error(error.response?.data.server_response || 'Something went wrong');
        },
      },
    );
  });

  return (
    <ChannelSettingsView
      form={form}
      label={label}
      channelName={channelName}
      machineItems={mode === 'tv' ? TV_MACHINE_ITEMS : RADIO_MACHINE_ITEMS}
      folderPlaceholder={mode === 'tv' ? 'GMA7' : 'DZBB'}
      hasPrograms={!!representative}
      loading={loading}
      saving={updateMutation.isLoading}
      onRevert={handleRevert}
      onSave={handleSave}
    />
  );
};

export default ChannelSettingsContainer;
